import React, { useEffect, useMemo, useState } from 'react';
import Loader from '../../../components/loader/Loader';
import { backendApi } from '../../../api/backendClient';
import {
    fireErrorToast,
    fireSuccessToast,
} from '../../../utils/commonFunction';
import {
    MAX_TASK_SUBMIT_IMAGE_BYTES,
    MAX_TASK_SUBMIT_IMAGE_LABEL,
} from '../../../utils/attachmentUrl';
import { prepareEncryptedSubmissionPayload } from '../../../utils/walletTaskEncryption';
import { formFieldClass } from '../constants';
import {
    isTaskSubmitImageFile,
    isValidProofLink,
    MAX_TASK_SUBMIT_IMAGES,
    MAX_TASK_SUBMIT_LINKS,
} from '../utils/taskSubmissionUtils';

const RequiredMark = () => (
    <span className="text-rose-600 dark:text-rose-400" aria-hidden>
        {' '}
        *
    </span>
);

const OptionalMark = () => (
    <span className="ml-1 text-[10px] font-normal text-zinc-400 dark:text-zinc-500">
        (optional)
    </span>
);

const FieldError = ({ message }) =>
    message ? (
        <p
            className="mt-1 flex items-start gap-1 text-[11px] text-rose-600 dark:text-rose-400"
            role="alert"
        >
            <i className="fa-solid fa-circle-exclamation mt-0.5 text-[9px]" />
            <span>{message}</span>
        </p>
    ) : null;

const labelClass =
    'flex items-center gap-1 text-xs font-semibold text-zinc-800 dark:text-zinc-200';

const fieldMt = 'mt-1';

const emptyLinkRow = () => ({ id: crypto.randomUUID(), value: '' });

const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SubmitTaskForm = ({
    task,
    canSubmit,
    onCancel,
    onSubmitSuccess,
    onSubmitOnChain,
    backendToken,
    signMessageAsync,
    walletAddress,
}) => {
    const [message, setMessage] = useState('');
    const [proofLinks, setProofLinks] = useState([emptyLinkRow()]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [messageError, setMessageError] = useState('');
    const [linksError, setLinksError] = useState('');
    const [imagesError, setImagesError] = useState('');
    const [submitError, setSubmitError] = useState('');

    const previewUrls = useMemo(
        () =>
            selectedImages.map((item) => ({
                id: item.id,
                url: URL.createObjectURL(item.file),
            })),
        [selectedImages],
    );

    useEffect(
        () => () => {
            previewUrls.forEach((entry) => URL.revokeObjectURL(entry.url));
        },
        [previewUrls],
    );

    const updateLink = (id, value) => {
        setProofLinks((rows) =>
            rows.map((row) => (row.id === id ? { ...row, value } : row)),
        );
        if (linksError) setLinksError('');
    };

    const addLinkRow = () => {
        if (proofLinks.length >= MAX_TASK_SUBMIT_LINKS) {
            fireErrorToast(`You can add up to ${MAX_TASK_SUBMIT_LINKS} links.`);
            return;
        }
        setProofLinks((rows) => [...rows, emptyLinkRow()]);
    };

    const removeLinkRow = (id) => {
        setProofLinks((rows) => {
            const next = rows.filter((row) => row.id !== id);
            return next.length ? next : [emptyLinkRow()];
        });
    };

    const handleImagesChange = (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        const errors = [];
        const accepted = [];

        files.forEach((file) => {
            const mime = String(file.type || '').toLowerCase();
            if (mime && !mime.startsWith('image/')) {
                errors.push(`"${file.name}" is not an image.`);
                return;
            }
            if (file.size > MAX_TASK_SUBMIT_IMAGE_BYTES) {
                errors.push(
                    `"${file.name}" exceeds ${MAX_TASK_SUBMIT_IMAGE_LABEL} (${formatBytes(file.size)}).`,
                );
                return;
            }
            if (!isTaskSubmitImageFile(file)) {
                errors.push(`"${file.name}" is not a supported image.`);
                return;
            }
            accepted.push({
                id: crypto.randomUUID(),
                file,
                name: file.name,
                size: file.size,
            });
        });

        if (errors.length) {
            fireErrorToast(errors[0]);
            if (errors.length > 1) {
                setImagesError(errors.slice(0, 3).join(' '));
            }
        } else {
            setImagesError('');
        }

        if (!accepted.length) return;

        setSelectedImages((prev) => {
            const combined = [...prev, ...accepted];
            if (combined.length > MAX_TASK_SUBMIT_IMAGES) {
                fireErrorToast(
                    `You can upload up to ${MAX_TASK_SUBMIT_IMAGES} images.`,
                );
                return combined.slice(0, MAX_TASK_SUBMIT_IMAGES);
            }
            return combined;
        });
    };

    const removeImage = (id) => {
        setSelectedImages((prev) => prev.filter((item) => item.id !== id));
        setImagesError('');
    };

    const collectValidLinks = () => {
        const trimmed = proofLinks
            .map((row) => String(row.value || '').trim())
            .filter(Boolean);
        const invalid = trimmed.filter((url) => !isValidProofLink(url));
        return { trimmed, invalid };
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitError('');
        setMessageError('');
        setLinksError('');
        setImagesError('');

        if (!message.trim()) {
            setMessageError('Enter a submission message.');
            return;
        }

        if (!canSubmit) {
            setSubmitError(
                'Accept the task first, then confirm on-chain escrow (if funded), before submitting work.',
            );
            return;
        }

        if (!signMessageAsync || !walletAddress) {
            setSubmitError('Connect your wallet to encrypt and submit work.');
            return;
        }

        const companyWallet =
            task?.company?.ownerWallet || task?.createdByWallet || '';
        if (!companyWallet) {
            setSubmitError('Company wallet is missing for this task.');
            return;
        }

        const { trimmed: linkUrls, invalid } = collectValidLinks();
        if (invalid.length) {
            setLinksError('Enter valid http(s) URLs for each proof link.');
            return;
        }

        try {
            setLoading(true);

            const encryptedFields = await prepareEncryptedSubmissionPayload({
                signMessageAsync,
                employeeWalletAddress: walletAddress,
                companyWalletAddress: companyWallet,
                message: message.trim(),
                links: linkUrls,
                imageFiles: selectedImages.map((item) => item.file),
                registerEncryptionPublicKey: (encryptionPublicKey) =>
                    backendApi.registerEncryptionPublicKey(
                        backendToken,
                        encryptionPublicKey,
                    ),
                fetchEncryptionPublicKey: (targetWallet) =>
                    backendApi.getEncryptionPublicKey(backendToken, targetWallet),
                uploadEncryptedFile: (encFile) =>
                    backendApi.uploadTaskFile(backendToken, encFile),
            });

            const chainSubmission =
                typeof onSubmitOnChain === 'function'
                    ? await onSubmitOnChain({
                          task,
                          message: encryptedFields.message,
                          attachments: encryptedFields.attachments,
                          encryptionCommitment:
                              encryptedFields.encryptedPayload?.ciphertext ||
                              null,
                      })
                    : null;

            await backendApi.submitTask(backendToken, task.id, {
                ...encryptedFields,
                submitTxHash: chainSubmission?.txHash,
                submissionURI: chainSubmission?.submissionURI,
                submissionHash: chainSubmission?.submissionHash,
            });

            fireSuccessToast('Encrypted work submitted successfully.');
            onSubmitSuccess();
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Submission failed.';
            setSubmitError(msg);
            fireErrorToast(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="submit-task-form"
            className="rounded-xl border border-teal-200/80 bg-teal-50/30 p-5 dark:border-teal-500/25 dark:bg-teal-950/20"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                        Submit your work
                    </h2>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                        Message, links, and images are encrypted for company +
                        employee only (max {MAX_TASK_SUBMIT_IMAGE_LABEL} each).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-600 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                    <i className="fa-solid fa-xmark text-[10px]" />
                    Close
                </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4" noValidate>
                {submitError ? (
                    <div
                        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-100"
                        role="alert"
                    >
                        {submitError}
                    </div>
                ) : null}

                <div>
                    <label htmlFor="submit-message" className={labelClass}>
                        Message
                        <RequiredMark />
                    </label>
                    <textarea
                        id="submit-message"
                        rows={4}
                        value={message}
                        onChange={(e) => {
                            setMessage(e.target.value);
                            if (messageError) setMessageError('');
                        }}
                        placeholder="Describe what you delivered and how to verify it."
                        className={`${formFieldClass} ${fieldMt} min-h-[88px] resize-y ${
                            messageError
                                ? 'border-rose-400/70 focus:border-rose-500 focus:ring-rose-500/30 dark:border-rose-500/50'
                                : ''
                        }`}
                        disabled={!canSubmit || loading}
                        aria-invalid={Boolean(messageError)}
                    />
                    <FieldError message={messageError} />
                </div>

                <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <label className={labelClass}>
                            Proof links
                            <OptionalMark />
                        </label>
                        <button
                            type="button"
                            onClick={addLinkRow}
                            disabled={
                                !canSubmit ||
                                loading ||
                                proofLinks.length >= MAX_TASK_SUBMIT_LINKS
                            }
                            className="text-[11px] font-semibold text-teal-700 hover:text-teal-800 disabled:opacity-40 dark:text-teal-400 dark:hover:text-teal-300"
                        >
                            <i className="fa-solid fa-plus mr-1 text-[9px]" />
                            Add link
                        </button>
                    </div>
                    <p className="mt-0.5 text-[10px] text-zinc-500">
                        Up to {MAX_TASK_SUBMIT_LINKS} URLs (repo, demo, docs, etc.)
                    </p>
                    <ul className={`${fieldMt} space-y-2`}>
                        {proofLinks.map((row, index) => (
                            <li key={row.id} className="flex gap-2">
                                <input
                                    type="url"
                                    value={row.value}
                                    onChange={(e) =>
                                        updateLink(row.id, e.target.value)
                                    }
                                    placeholder={`https://example.com/proof-${index + 1}`}
                                    className={`${formFieldClass} min-w-0 flex-1`}
                                    disabled={!canSubmit || loading}
                                />
                                {proofLinks.length > 1 ? (
                                    <button
                                        type="button"
                                        onClick={() => removeLinkRow(row.id)}
                                        disabled={!canSubmit || loading}
                                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 dark:border-zinc-700 dark:hover:border-rose-500/40 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                                        aria-label="Remove link"
                                    >
                                        <i className="fa-solid fa-trash-can text-xs" />
                                    </button>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                    <FieldError message={linksError} />
                </div>

                <div>
                    <label className={labelClass}>
                        Proof images
                        <OptionalMark />
                    </label>
                    <p className="mt-0.5 text-[10px] text-zinc-500">
                        Images only · encrypted before upload · max{' '}
                        {MAX_TASK_SUBMIT_IMAGE_LABEL} each · up to{' '}
                        {MAX_TASK_SUBMIT_IMAGES} files
                    </p>
                    <div
                        className={`${fieldMt} group relative cursor-pointer rounded-lg border border-dashed border-zinc-300 bg-white p-4 text-center transition hover:border-teal-400 dark:border-zinc-600 dark:bg-zinc-950/60 dark:hover:border-teal-500/40 ${
                            !canSubmit ||
                            loading ||
                            selectedImages.length >= MAX_TASK_SUBMIT_IMAGES
                                ? 'pointer-events-none opacity-60'
                                : ''
                        }`}
                    >
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/bmp,image/svg+xml"
                            multiple
                            onChange={handleImagesChange}
                            className="absolute inset-0 cursor-pointer opacity-0"
                            disabled={
                                !canSubmit ||
                                loading ||
                                selectedImages.length >= MAX_TASK_SUBMIT_IMAGES
                            }
                        />
                        <div className="flex flex-col items-center gap-2">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-100 group-hover:bg-teal-100 dark:bg-zinc-800 dark:group-hover:bg-teal-500/10">
                                <i className="fa-solid fa-images text-sm text-zinc-500 group-hover:text-teal-700 dark:text-zinc-400 dark:group-hover:text-teal-400" />
                            </div>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">
                                Click to add images (multiple allowed)
                            </p>
                        </div>
                    </div>
                    <FieldError message={imagesError} />

                    {selectedImages.length > 0 ? (
                        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                            {selectedImages.map((item) => {
                                const preview = previewUrls.find(
                                    (p) => p.id === item.id,
                                );
                                return (
                                    <li
                                        key={item.id}
                                        className="flex gap-2 rounded-lg border border-teal-200/80 bg-white p-2 dark:border-teal-500/25 dark:bg-zinc-950/60"
                                    >
                                        {preview ? (
                                            <img
                                                src={preview.url}
                                                alt=""
                                                className="h-14 w-14 shrink-0 rounded-md object-cover ring-1 ring-zinc-200 dark:ring-zinc-700"
                                            />
                                        ) : null}
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs font-medium text-zinc-900 dark:text-zinc-100">
                                                {item.name}
                                            </p>
                                            <p className="text-[10px] text-zinc-500">
                                                {formatBytes(item.size)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeImage(item.id)}
                                            disabled={loading}
                                            className="self-center text-zinc-500 hover:text-rose-600 dark:text-zinc-400 dark:hover:text-rose-400"
                                            aria-label={`Remove ${item.name}`}
                                        >
                                            <i className="fa-solid fa-xmark text-xs" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-teal-200/60 pt-4 dark:border-teal-500/20">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-lg border border-zinc-200 px-4 py-2 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                        Cancel
                    </button>
                    {loading ? (
                        <div className="flex min-w-[140px] flex-1 items-center justify-center rounded-lg bg-zinc-100 py-2 dark:bg-zinc-800">
                            <Loader size="20px" stroke="#0d9488" />
                        </div>
                    ) : (
                        <button
                            type="submit"
                            disabled={!canSubmit || !message.trim()}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-500 disabled:opacity-40 dark:bg-teal-600 dark:hover:bg-teal-500 sm:flex-none sm:px-6"
                        >
                            <i className="fa-solid fa-paper-plane" />
                            Submit encrypted work
                        </button>
                    )}
                </div>
            </form>
        </section>
    );
};

export default SubmitTaskForm;
